/**
 * Authentication Context
 * Manages user authentication state
 *
 * Zwei Arten von Zugaengen: Mitglieder/Owner (role Client/Team/Owner, /api/auth)
 * und Partner (Physios, role Partner, /api/partner). Beide liegen unter
 * denselben localStorage-Schluesseln, unterschieden wird ueber user.role.
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, partnerAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in, then refresh from server for fresh data (e.g. studioName)
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      const parsed = JSON.parse(savedUser);
      setUser(parsed);

      const refresh = parsed?.role === 'Partner' ? partnerAPI.me() : authAPI.getCurrentUser();

      // Refresh user data from server (updates studioName, role, token etc.)
      refresh.then(response => {
        const freshUser = response.data;
        // If the server issued a fresh token (e.g. role changed), update it
        if (freshUser.token) {
          localStorage.setItem('token', freshUser.token);
          delete freshUser.token; // don't store token in user object
        }
        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
      }).catch(() => {
        // Token expired or invalid — log out
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      });
    }
    setLoading(false);
  }, []);

  const speichern = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
  };

  /** bereich: 'mitglied' (Standard) oder 'partner' */
  const login = async (email, password, bereich = 'mitglied') => {
    try {
      const response = bereich === 'partner'
        ? await partnerAPI.login(email, password)
        : await authAPI.login(email, password);
      const { token, user } = response.data;
      speichern(token, user);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  /** Nach Profilaenderung im Partnerbereich (neue E-Mail -> neues Token) */
  const aktualisiereUser = (neuerUser, neuesToken) => {
    speichern(neuesToken || localStorage.getItem('token'), neuerUser);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isOwner = () => user?.role === 'Owner';
  const isSuperuser = () => !!user?.isSuperuser;
  const isPartner = () => user?.role === 'Partner';

  const value = {
    user,
    login,
    logout,
    aktualisiereUser,
    isOwner,
    isSuperuser,
    isPartner,
    isAuthenticated: !!user,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
